import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { FlattenSimpleInterpolation } from 'styled-components';
import tw from 'tailwind.macro';
import { IDiscountCard } from '../../../common/types';
import { CheckboxRadioInput } from '../../Forms/Input';
import FieldLabelControl from '../../Forms/Input/FieldLabelControl';
import { PanelScrollableWrapper } from '../../Funnel/Panel/common';
import SecondaryPanel from '../../Funnel/Panel/SecondaryPanel';
import {
  TSB_DISCOUNT_CARD_PANEL_TEST_ID,
  TSB_DISCOUNT_CARD_TEST_ID,
} from '../constants';
import messages from '../messages';

const Container = styled.div<{ customStyle?: FlattenSimpleInterpolation }>`
  ${tw`absolute z-1`}
  inset: 0;
  ${({ customStyle }) => customStyle}
`;

const Wrapper = styled(PanelScrollableWrapper)`
  ${tw`pb-medium flex flex-col`}
  gap:var(--size-medium);
`;

const NoDiscountCard = styled.div`
  ${tw`sticky bg-white`}
  top:0;
`;

interface DiscountCardsPanelProps {
  onClose: () => void;
  selectedDiscountCard: IDiscountCard | null;
  onDiscountCardChange: (discountCard: IDiscountCard) => void;
  getDiscountCards: () => Promise<IDiscountCard[]>;
  customStyle?: FlattenSimpleInterpolation;
}

/**
 * @description Returns a component that displays a panel to modify a passenger's reduction cards.
 * @returns {React.FC<DiscountCardsPanelProps>}
 */
const DiscountCardsPanel: React.FC<DiscountCardsPanelProps> = ({
  customStyle,
  getDiscountCards,
  onClose,
  onDiscountCardChange,
  selectedDiscountCard,
}) => {
  const [discountCards, setDiscountCards] = useState<IDiscountCard[]>([]);
  const overlayCloseButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    getDiscountCards().then(setDiscountCards);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container customStyle={customStyle}>
      <SecondaryPanel
        closeButtonRef={overlayCloseButtonRef}
        onClose={onClose}
        testid={TSB_DISCOUNT_CARD_PANEL_TEST_ID}
        title={
          <FormattedMessage {...messages.discountCardsPanelOverlayTitle} />
        }
      >
        <Wrapper>
          <NoDiscountCard>
            <FieldLabelControl
              control={
                <CheckboxRadioInput
                  checked={!selectedDiscountCard}
                  isRounded
                  name="discountCards"
                  onChange={() => {
                    onDiscountCardChange(null);
                    overlayCloseButtonRef.current?.click();
                  }}
                  type="radio"
                />
              }
              isHighlighted={selectedDiscountCard === null}
              label={
                <FormattedMessage
                  {...messages.discountCardsPanelNoDiscountCard}
                />
              }
              labelPlacement={'start'}
              spaceBetween
            />
          </NoDiscountCard>

          <DiscountCardsGroup
            discountCards={discountCards.reduce((acc, discountCard) => {
              if (selectedDiscountCard?.id === discountCard.id) {
                // Move the selected discount card to the top of the list
                return [discountCard, ...acc];
              }

              return [...acc, discountCard];
            }, [])}
            onDiscountCardChange={(dicountCard) => {
              onDiscountCardChange(dicountCard);
              overlayCloseButtonRef.current?.click();
            }}
            selectedDiscountCard={selectedDiscountCard}
          />
        </Wrapper>
      </SecondaryPanel>
    </Container>
  );
};

const DiscountCardsGroupContainer = styled.div`
  ${tw`flex flex-col`}
  gap:var(--size-medium)
`;

const DiscountCardsGroupTitle = styled.span`
  ${tw`font-bold font-kyrial text-title-medium-m leading-title-medium-m
    md:text-title-small md:leading-title-small
  `}
`;

const DiscountCardsListWrapper = styled.div`
  ${tw`flex flex-col`}
  gap:var(--size-small)
`;

interface DiscountCardsGroupProps {
  onDiscountCardChange: (discountCard) => void;
  selectedDiscountCard: IDiscountCard;
  discountCards: IDiscountCard[];
}

const DiscountCardsGroup: React.FC<DiscountCardsGroupProps> = ({
  discountCards,
  onDiscountCardChange,
  selectedDiscountCard,
}) => (
  <DiscountCardsGroupContainer>
    <DiscountCardsGroupTitle>SNCF</DiscountCardsGroupTitle>

    <DiscountCardsListWrapper>
      {discountCards.map((discountCard, index) => {
        const isChecked = selectedDiscountCard?.id === discountCard.id;

        return (
          <FieldLabelControl
            key={index}
            control={
              <CheckboxRadioInput
                checked={isChecked}
                data-testid={TSB_DISCOUNT_CARD_TEST_ID}
                isRounded
                name="discountCards"
                onChange={() => onDiscountCardChange(discountCard)}
                type="radio"
              />
            }
            isHighlighted={isChecked}
            label={discountCard.label}
            labelPlacement={'start'}
            spaceBetween
          />
        );
      })}
    </DiscountCardsListWrapper>
  </DiscountCardsGroupContainer>
);

export default DiscountCardsPanel;
